import { useState } from 'react'
import { router } from '@inertiajs/react'
import { NotaryPortalLayout } from '@/components/layout/NotaryPortalLayout'
import { DataTable } from '@/components/backoffice/DataTable'
import { Badge } from '@/components/ui/badge'
import { PortalAppointment } from '@/types'

type Props = {
  upcoming_appointments: PortalAppointment[]
  past_appointments: PortalAppointment[]
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function StatusBadge({ cancelled }: { cancelled: boolean }) {
  if (cancelled) {
    return <Badge className="bg-red-100 text-red-700 border border-red-200/60 text-[11px] font-normal">Cancelled</Badge>
  }
  return <Badge variant="outline" className="text-[11px] font-normal">Scheduled</Badge>
}

const columns = [
  {
    key: 'business',
    header: 'Business',
    className: 'flex-1 min-w-0',
    render: (a: PortalAppointment) => (
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{a.business_name}</p>
        <p className="text-[12px] text-muted-foreground truncate">{a.account_name}</p>
      </div>
    ),
  },
  {
    key: 'datetime',
    header: 'Date & Time',
    className: 'w-44',
    render: (a: PortalAppointment) => (
      <span className="text-sm text-muted-foreground">{formatDateTime(a.scheduled_at)}</span>
    ),
  },
  {
    key: 'mode',
    header: 'Mode',
    className: 'w-28',
    render: (a: PortalAppointment) => (
      <Badge variant="outline" className="text-[11px] capitalize font-normal">
        {a.session_mode.replace(/_/g, ' ')}
      </Badge>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    className: 'w-24',
    render: (a: PortalAppointment) => <StatusBadge cancelled={a.cancelled} />,
  },
]

export default function AppointmentsIndex({ upcoming_appointments, past_appointments }: Props) {
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming')

  const activeData = tab === 'upcoming' ? upcoming_appointments : past_appointments

  return (
    <NotaryPortalLayout title="Appointments">
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Appointments</h1>
          <p className="text-sm text-muted-foreground">All your scheduled notarization appointments</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-border/40">
          <button
            type="button"
            onClick={() => setTab('upcoming')}
            className={`px-3 py-2 text-sm transition-colors border-b-2 -mb-px ${
              tab === 'upcoming'
                ? 'border-foreground font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Upcoming ({upcoming_appointments.length})
          </button>
          <button
            type="button"
            onClick={() => setTab('past')}
            className={`px-3 py-2 text-sm transition-colors border-b-2 -mb-px ${
              tab === 'past'
                ? 'border-foreground font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Past ({past_appointments.length})
          </button>
        </div>

        <DataTable
          data={activeData}
          keyFn={(a) => a.id}
          emptyMessage={tab === 'upcoming' ? 'No upcoming appointments.' : 'No past appointments.'}
          onRowClick={(a) => router.visit(`/notary_portal/appointments/${a.id}`)}
          columns={columns}
        />
      </div>
    </NotaryPortalLayout>
  )
}
